import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { 
  Bell, Check, CheckCheck, Trash2, Settings, X,
  AlertTriangle, FileText, GraduationCap, Shield, Clock,
  Users, Calendar, ClipboardCheck, AlertCircle, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Notification type configurations
const NOTIFICATION_TYPES = {
  permit_expiring: { 
    icon: FileText, 
    color: 'text-orange-500', 
    bgColor: 'bg-orange-50',
    label: 'Permit Expiring'
  },
  permit_expired: { 
    icon: AlertTriangle, 
    color: 'text-red-500', 
    bgColor: 'bg-red-50',
    label: 'Permit Expired'
  },
  training_expiring: { 
    icon: GraduationCap, 
    color: 'text-yellow-500', 
    bgColor: 'bg-yellow-50',
    label: 'Training Expiring'
  },
  training_expired: { 
    icon: GraduationCap, 
    color: 'text-red-500', 
    bgColor: 'bg-red-50',
    label: 'Training Expired'
  },
  inspection_due: { 
    icon: ClipboardCheck, 
    color: 'text-blue-500', 
    bgColor: 'bg-blue-50',
    label: 'Inspection Due'
  },
  inspection_overdue: { 
    icon: ClipboardCheck, 
    color: 'text-red-500', 
    bgColor: 'bg-red-50',
    label: 'Inspection Overdue'
  },
  incident_reported: { 
    icon: AlertCircle, 
    color: 'text-red-500', 
    bgColor: 'bg-red-50',
    label: 'Incident Reported'
  },
  ptp_signoff: { 
    icon: Users, 
    color: 'text-purple-500', 
    bgColor: 'bg-purple-50',
    label: 'PTP Sign-off Required'
  },
  compliance_deadline: { 
    icon: Calendar, 
    color: 'text-indigo-500', 
    bgColor: 'bg-indigo-50',
    label: 'Compliance Deadline'
  },
  safety_alert: { 
    icon: Shield, 
    color: 'text-red-500', 
    bgColor: 'bg-red-50',
    label: 'Safety Alert'
  },
  cctv_alert: { 
    icon: AlertTriangle, 
    color: 'text-red-600', 
    bgColor: 'bg-red-100',
    label: 'CCTV Safety Alert'
  },
  cctv_ppe_violation: { 
    icon: Shield, 
    color: 'text-orange-600', 
    bgColor: 'bg-orange-100',
    label: 'PPE Violation Detected'
  },
  cctv_hazard: { 
    icon: AlertTriangle, 
    color: 'text-red-600', 
    bgColor: 'bg-red-100',
    label: 'Hazard Detected'
  },
  info: { 
    icon: Info, 
    color: 'text-blue-500', 
    bgColor: 'bg-blue-50',
    label: 'Information'
  }
};

// Format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Single Notification Item
const NotificationItem = ({ notification, onMarkRead, onDelete }) => {
  const config = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.info;
  const Icon = config.icon;

  const handleClick = () => {
    if (notification.link) {
      window.location.href = notification.link;
    }
  };

  const getSeverityBadge = () => {
    if (!notification.severity) return null;
    const colors = {
      critical: 'bg-red-500 text-white',
      high: 'bg-orange-500 text-white',
      medium: 'bg-yellow-500 text-black',
      low: 'bg-green-500 text-white'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[notification.severity] || 'bg-gray-200'}`}>
        {notification.severity}
      </span>
    );
  };

  return (
    <div 
      className={`p-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer ${
        !notification.read ? config.bgColor : ''
      }`}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${config.bgColor}`}>
          <Icon className={`w-4 h-4 ${config.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2">
                <p className={`text-sm ${!notification.read ? 'font-semibold' : ''}`}>
                  {notification.title}
                </p>
                {getSeverityBadge()}
              </div>
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                {notification.message}
              </p>
              {notification.feed_name && (
                <p className="text-xs text-blue-500 mt-1">
                  📹 {notification.feed_name} {notification.location && `• ${notification.location}`}
                </p>
              )}
            </div>
            {!notification.read && (
              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
            )}
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-gray-400">
              {formatRelativeTime(notification.created_at)}
            </span>
            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
              {!notification.read && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-6 px-2 text-xs"
                  onClick={() => onMarkRead(notification.id)}
                >
                  <Check className="w-3 h-3" />
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2 text-xs text-red-500 hover:text-red-700"
                onClick={() => onDelete(notification.id)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Notification Center Component
const NotificationCenter = ({ isOpen, onClose, reminderSummary }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread

  const fetchNotifications = useCallback(async () => {
    try {
      // Fetch both regular notifications and CCTV alerts
      const [notifResponse, cctvAlertsResponse] = await Promise.all([
        axios.get(`${API}/api/notifications`).catch(() => ({ data: { notifications: [] } })),
        axios.get(`${API}/api/cctv-monitoring/alerts?status=new&limit=10`).catch(() => ({ data: { alerts: [] } }))
      ]);
      
      const regularNotifications = notifResponse.data.notifications || [];
      const cctvAlerts = (cctvAlertsResponse.data.alerts || []).map(alert => ({
        id: `cctv_${alert.id}`,
        type: 'cctv_alert',
        title: `CCTV Alert: ${(alert.alert_type || 'hazard').replace(/_/g, ' ')}`,
        message: alert.description || 'Safety concern detected via CCTV',
        read: alert.status !== 'new',
        created_at: alert.timestamp,
        link: '/cctv-monitoring',
        severity: alert.severity,
        feed_name: alert.feed_name,
        location: alert.location
      }));
      
      // Combine and sort by created_at
      const allNotifications = [...regularNotifications, ...cctvAlerts]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setNotifications(allNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Demo notifications for testing
      setNotifications([
        {
          id: '1',
          type: 'cctv_alert',
          title: 'CCTV Alert: PPE Violation',
          message: 'Worker detected without hard hat in Loading Dock A',
          read: false,
          created_at: new Date(Date.now() - 900000).toISOString(),
          link: '/cctv-monitoring',
          severity: 'high'
        },
        {
          id: '2',
          type: 'permit_expiring',
          title: 'Hot Work Permit Expiring',
          message: 'Permit #HW-2026-001 expires in 2 days. Please renew or close.',
          read: false,
          created_at: new Date(Date.now() - 1800000).toISOString(),
          link: '/permits'
        },
        {
          id: '3',
          type: 'training_expiring',
          title: 'Forklift Certification Expiring',
          message: 'John Smith\'s forklift certification expires in 7 days.',
          read: false,
          created_at: new Date(Date.now() - 3600000).toISOString(),
          link: '/training-management'
        },
        {
          id: '4',
          type: 'incident_reported',
          title: 'New Incident Reported',
          message: 'A near-miss incident was reported in Warehouse B.',
          read: false,
          created_at: new Date(Date.now() - 7200000).toISOString(),
          link: '/incidents'
        },
        {
          id: '5',
          type: 'ptp_signoff',
          title: 'PTP Awaiting Signature',
          message: 'Daily PTP for Site A requires your signature.',
          read: true,
          created_at: new Date(Date.now() - 86400000).toISOString(),
          link: '/daily-ptp'
        },
        {
          id: '5',
          type: 'compliance_deadline',
          title: 'Fire Extinguisher Inspection Due',
          message: 'Monthly fire extinguisher inspection is due in 3 days.',
          read: true,
          created_at: new Date(Date.now() - 172800000).toISOString(),
          link: '/compliance-calendar'
        },
        {
          id: '6',
          type: 'inspection_overdue',
          title: 'Safety Audit Overdue',
          message: 'Quarterly safety audit for Building C is 5 days overdue.',
          read: false,
          created_at: new Date(Date.now() - 259200000).toISOString(),
          link: '/audits'
        }
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/api/notifications/${notificationId}/read`);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
    setNotifications(notifications.map(n => 
      n.id === notificationId ? { ...n, read: true } : n
    ));
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/api/notifications/read-all`);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
    setNotifications(notifications.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/api/notifications/${notificationId}`);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
    setNotifications(notifications.filter(n => n.id !== notificationId));
  };

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20" />
      <div 
        className="absolute right-4 top-16 w-96 max-h-[80vh] bg-white rounded-xl shadow-2xl border overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Bell className="w-5 h-5" />
              <h3 className="font-semibold">Notifications</h3>
              {unreadCount > 0 && (
                <Badge className="bg-white text-blue-600">{unreadCount}</Badge>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-white hover:bg-white/20"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Filter tabs */}
          <div className="flex gap-2 mt-3">
            <Button 
              size="sm" 
              variant={filter === 'all' ? 'secondary' : 'ghost'}
              className={filter === 'all' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/20'}
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button 
              size="sm" 
              variant={filter === 'unread' ? 'secondary' : 'ghost'}
              className={filter === 'unread' ? 'bg-white text-blue-600' : 'text-white hover:bg-white/20'}
              onClick={() => setFilter('unread')}
            >
              Unread ({unreadCount})
            </Button>
          </div>
        </div>

        {/* Reminder Summary */}
        {reminderSummary && (reminderSummary.total_urgent > 0 || reminderSummary.total_upcoming > 0) && (
          <div className="mx-3 mb-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Action Required
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {reminderSummary.overdue_capas > 0 && (
                <a href="/capa" className="flex items-center gap-1 text-red-600 hover:underline">
                  <ClipboardCheck className="w-3 h-3" />
                  {reminderSummary.overdue_capas} overdue CAPA{reminderSummary.overdue_capas > 1 ? 's' : ''}
                </a>
              )}
              {reminderSummary.overdue_checklists > 0 && (
                <a href="/osha-checklists" className="flex items-center gap-1 text-amber-600 hover:underline">
                  <Clock className="w-3 h-3" />
                  {reminderSummary.overdue_checklists} checklist{reminderSummary.overdue_checklists > 1 ? 's' : ''} due
                </a>
              )}
              {reminderSummary.upcoming_capas > 0 && (
                <a href="/capa" className="flex items-center gap-1 text-blue-600 hover:underline">
                  <ClipboardCheck className="w-3 h-3" />
                  {reminderSummary.upcoming_capas} CAPA{reminderSummary.upcoming_capas > 1 ? 's' : ''} due soon
                </a>
              )}
              {reminderSummary.overdue_training > 0 && (
                <a href="/training" className="flex items-center gap-1 text-purple-600 hover:underline">
                  <GraduationCap className="w-3 h-3" />
                  {reminderSummary.overdue_training} training overdue
                </a>
              )}
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Bell className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            filteredNotifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={markAsRead}
                onDelete={deleteNotification}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t bg-gray-50 flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
            >
              <CheckCheck className="w-3 h-3 mr-1" /> Mark all read
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs"
              onClick={() => window.location.href = '/notification-settings'}
            >
              <Settings className="w-3 h-3 mr-1" /> Settings
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Notification Bell Button (for header)
export const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [reminderSummary, setReminderSummary] = useState(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      // Fetch notification count, CCTV alerts, and reminder summary
      const [notifResponse, cctvResponse, reminderResponse] = await Promise.all([
        axios.get(`${API}/api/notifications/unread-count`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${API}/api/cctv-monitoring/alerts?status=new&limit=1`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${API}/api/notifications/reminders/summary`).catch(() => ({ data: { summary: null } }))
      ]);
      
      const regularCount = notifResponse.data.count || 0;
      const cctvCount = cctvResponse.data.total || 0;
      const summary = reminderResponse.data.summary;
      
      setUnreadCount(regularCount + cctvCount);
      setReminderSummary(summary);
    } catch (error) {
      // Demo count
      setUnreadCount(4);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const totalUrgent = reminderSummary?.total_urgent || 0;
  const displayCount = unreadCount + totalUrgent;

  return (
    <>
      <Button 
        variant="ghost" 
        size="sm" 
        className="relative"
        onClick={() => setIsOpen(true)}
        data-testid="notification-bell-btn"
      >
        <Bell className={`w-5 h-5 ${displayCount > 0 ? 'text-amber-500' : ''}`} />
        {displayCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
            {displayCount > 9 ? '9+' : displayCount}
          </span>
        )}
      </Button>
      <NotificationCenter isOpen={isOpen} onClose={() => setIsOpen(false)} reminderSummary={reminderSummary} />
    </>
  );
};

export default NotificationCenter;
